import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import { MdDelete } from 'react-icons/md';
import { Button, Chip, Grid, IconButton, Paper } from '@material-ui/core';
import { TextField, Typography } from '@material-ui/core';

import API from 'utils/api';
import { ROLES } from 'consts';
import { useAuth, useSources } from 'hooks';
import { ButtonContainer, ErrorDialog, Form, Table, Tooltip } from 'components';
import AddUserDialog from './AddUserDialog';

import styles from './GroupForm.module.scss';

const validate = Yup.object().shape({
  name: Yup.string().required('Required'),
  description: Yup.string().required('Required'),
});

const GroupForm = props => {
  const [groupData, setGroupData] = useState();
  const [userList, setUserList] = useState([]);
  const [dialog, setDialog] = useState({});

  const auth = useAuth();
  const sources = useSources();
  const editId = props.match.params.id;

  useEffect(() => {
    if (editId) {
      API.getGroup(editId, sources).then(({ data }) => setGroupData(data || null));
    } else {
      const { _id: id, fullname: name } = auth.user;

      setGroupData({
        name: '',
        description: '',
        users: [{ id, name, roles: [ROLES.ADMIN] }],
      });
    }

    API.getUsers({ select: 'fullname' }, sources).then(({ data }) => {
      if (data && data.docs) {
        setUserList(data.docs.map(({ _id: id, fullname: name }) => ({ id, name })));
      }
    });
  }, [editId, sources, auth]);

  if (groupData === undefined) {
    return null;
  }

  if (groupData == null) {
    props.history.push('/dashboard/users', { selectedTab: 1 });
  }

  const onSubmit = async ({ name, description }, { setSubmitting }) => {
    const data = {
      name,
      description,
      users: groupData.users,
      id: groupData._id,
    };

    const { error } = data.id
      ? await API.editGroup(data, sources)
      : await API.createGroup(data, sources);

    setSubmitting(false);

    if (error) {
      setDialog({ error });
    } else {
      props.history.push('/dashboard/users', { selectedTab: 1 });
    }
  };

  const addUserToGroup = ({ id, name }, roles, data) => {
    const found = groupData.users.find(it => it.id === id);

    if (!found) {
      setGroupData({
        ...groupData,
        users: [...groupData.users, { id, name, roles, data }],
      });
    }
  };

  const removeUserFromGroup = userId =>
    setGroupData({
      ...groupData,
      users: groupData.users.filter(({ id }) => id !== userId),
    });

  const { _id, name, description } = groupData;

  const availableUsers = userList.filter(
    ({ id: i1 }) => !groupData.users.find(({ id: i2 }) => i1 === i2)
  );

  const renderUsersTable = () => {
    return (
      <>
        <div className={styles.topbar}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setDialog({ add: true })}
            disabled={!availableUsers.length}
          >
            add user
          </Button>
        </div>

        <Table
          columns={[
            {
              title: 'User',
              field: 'name',
              weight: 3,
              defaultSort: 'desc',
            },
            {
              title: 'Roles',
              weight: 3,
              sorting: false,
              render: ({ roles }) => (
                <div className={styles.rolesWrapper}>
                  {roles
                    .filter(it => !!it)
                    .map(role => (
                      <Chip key={role} label={role} />
                    ))}
                </div>
              ),
            },
            {
              title: 'Actions',
              sorting: false,
              render: ({ id }) => (
                <IconButton
                  disabled={auth.user._id === id}
                  color="primary"
                  onClick={() => removeUserFromGroup(id)}
                >
                  <Tooltip title="Remove user from group" placement="top">
                    <MdDelete />
                  </Tooltip>
                </IconButton>
              ),
            },
          ]}
          data={groupData.users}
        />
      </>
    );
  };

  const onCloseDialog = () => setDialog({});

  return (
    <div className={styles.groupForm}>
      <Typography variant="h6" gutterBottom>
        {_id ? 'Edit group' : 'Create new group'}
      </Typography>

      <Form
        validationSchema={validate}
        initialValues={{ name, description }}
        onSubmit={onSubmit}
      >
        {({ fieldProps, isSubmitting }) => (
          <Grid item xs={12}>
            <Paper className={styles.paper}>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <TextField {...fieldProps('name', 'Group Name')} />
                </Grid>

                <Grid item xs={6}>
                  <TextField {...fieldProps('description', 'Description')} />
                </Grid>
              </Grid>
            </Paper>

            <Paper className={styles.paper}>{renderUsersTable()}</Paper>

            <ButtonContainer>
              <Button
                onClick={() =>
                  props.history.push('/dashboard/users', {
                    selectedTab: 1,
                  })
                }
              >
                cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isSubmitting}
              >
                {_id ? 'save' : 'create'}
              </Button>
            </ButtonContainer>
          </Grid>
        )}
      </Form>

      <AddUserDialog
        open={!!dialog.add}
        onClose={onCloseDialog}
        users={availableUsers}
        onAdd={(user, roles, influencerCode) => {
          onCloseDialog();
          addUserToGroup(user, roles, { influencerCode });
        }}
      />

      <ErrorDialog open={!!dialog.error} onClose={onCloseDialog} message={dialog.error} />
    </div>
  );
};

export default GroupForm;
